// Shared location helpers for address forms (sign-up, billing, etc.).
//
// Countries / states / cities come from the already-installed `country-state-city`
// package (all ISO countries + their subdivisions). This module adds the one
// thing that package lacks: a per-country postal-code EXAMPLE + human-readable
// FORMAT, so a form can show a *local* placeholder once a country is chosen
// (instead of a hard-coded Australian "3000").
//
// Keyed by ISO-3166-1 alpha-2 (the same code country-state-city uses).
// `format` token convention: 9 = digit, A = letter.  Countries that have no
// postal system are intentionally absent -> getPostcodeHint() falls back to a
// generic hint and callers can treat the field as optional.

import { Country, State, City } from "country-state-city";

export { Country, State, City };

export const POSTCODE_INFO = {
  AU: { example: "2000", format: "9999" },
  US: { example: "10001", format: "99999 or 99999-9999" },
  GB: { example: "SW1A 1AA", format: "AA9A 9AA / A9 9AA" },
  CA: { example: "M5V 3L9", format: "A9A 9A9" },
  NZ: { example: "6011", format: "9999" },
  IE: { example: "D02 AF30", format: "A99 AAAA (Eircode)" },
  IN: { example: "110001", format: "999999" },
  DE: { example: "10115", format: "99999" },
  FR: { example: "75001", format: "99999" },
  IT: { example: "00184", format: "99999" },
  ES: { example: "28013", format: "99999" },
  PT: { example: "1000-001", format: "9999-999" },
  NL: { example: "1012 JS", format: "9999 AA" },
  BE: { example: "1000", format: "9999" },
  CH: { example: "8001", format: "9999" },
  AT: { example: "1010", format: "9999" },
  SE: { example: "111 29", format: "999 99" },
  NO: { example: "0150", format: "9999" },
  DK: { example: "1050", format: "9999" },
  FI: { example: "00100", format: "99999" },
  PL: { example: "00-001", format: "99-999" },
  CZ: { example: "110 00", format: "999 99" },
  SK: { example: "811 01", format: "999 99" },
  HU: { example: "1011", format: "9999" },
  RO: { example: "010011", format: "999999" },
  BG: { example: "1000", format: "9999" },
  GR: { example: "104 31", format: "999 99" },
  HR: { example: "10000", format: "99999" },
  SI: { example: "1000", format: "9999" },
  RS: { example: "11000", format: "99999" },
  UA: { example: "01001", format: "99999" },
  RU: { example: "101000", format: "999999" },
  TR: { example: "34122", format: "99999" },
  IS: { example: "101", format: "999" },
  LU: { example: "1009", format: "9999" },
  LT: { example: "01100", format: "99999" },
  LV: { example: "1050", format: "9999" },
  EE: { example: "10111", format: "99999" },
  CY: { example: "1010", format: "9999" },
  MT: { example: "VLT 1117", format: "AAA 9999" },
  JP: { example: "100-0001", format: "999-9999" },
  CN: { example: "100000", format: "999999" },
  KR: { example: "04524", format: "99999" },
  TW: { example: "100", format: "999 / 99999" },
  HK: { example: "", format: "No postal code" },
  SG: { example: "018956", format: "999999" },
  MY: { example: "50050", format: "99999" },
  TH: { example: "10100", format: "99999" },
  ID: { example: "10110", format: "99999" },
  PH: { example: "1000", format: "9999" },
  VN: { example: "100000", format: "999999" },
  BD: { example: "1000", format: "9999" },
  PK: { example: "44000", format: "99999" },
  LK: { example: "00100", format: "99999" },
  NP: { example: "44600", format: "99999" },
  KZ: { example: "010000", format: "999999" },
  AE: { example: "", format: "No postal code (use emirate)" },
  SA: { example: "11564", format: "99999 or 99999-9999" },
  QA: { example: "", format: "No postal code" },
  KW: { example: "13001", format: "99999" },
  BH: { example: "199", format: "999 / 9999" },
  OM: { example: "112", format: "999" },
  IL: { example: "9510001", format: "9999999" },
  JO: { example: "11118", format: "99999" },
  LB: { example: "1107 2020", format: "9999 9999" },
  EG: { example: "11511", format: "99999" },
  MA: { example: "10000", format: "99999" },
  DZ: { example: "16000", format: "99999" },
  TN: { example: "1000", format: "9999" },
  ZA: { example: "0001", format: "9999" },
  NG: { example: "100001", format: "999999" },
  KE: { example: "00100", format: "99999" },
  GH: { example: "", format: "No standard postal code" },
  ET: { example: "1000", format: "9999" },
  TZ: { example: "11101", format: "99999" },
  UG: { example: "", format: "No postal code" },
  MX: { example: "06000", format: "99999" },
  BR: { example: "01310-100", format: "99999-999" },
  AR: { example: "C1002AAP", format: "A9999AAA" },
  CL: { example: "8320000", format: "9999999" },
  CO: { example: "110111", format: "999999" },
  PE: { example: "15001", format: "99999" },
  VE: { example: "1010", format: "9999" },
  EC: { example: "170150", format: "999999" },
  UY: { example: "11000", format: "99999" },
  PY: { example: "1209", format: "9999" },
  BO: { example: "", format: "No postal code" },
  CR: { example: "10101", format: "99999" },
  PA: { example: "0801", format: "9999" },
  GT: { example: "01001", format: "99999" },
  DO: { example: "10101", format: "99999" },
  PR: { example: "00901", format: "99999" },
};

const GENERIC = { example: "", format: "" };

/** Postal-code hint for an ISO-2 country code: { example, format }. */
export function getPostcodeHint(iso2) {
  if (!iso2) return GENERIC;
  return POSTCODE_INFO[String(iso2).toUpperCase()] || GENERIC;
}

/** A ready-to-use input placeholder for the chosen country. */
export function getPostcodePlaceholder(iso2) {
  const hint = getPostcodeHint(iso2);
  if (hint.example) return hint.example;
  if (hint.format && hint.format.startsWith("No")) return "Not applicable";
  return "Postal / ZIP code";
}

/** True if the country has no postal system (so the field can be optional). */
export function countryHasNoPostcode(iso2) {
  const hint = getPostcodeHint(iso2);
  return !!hint.format && hint.format.startsWith("No");
}

/** All countries, sorted by name, as { code, name, flag }. */
export function getCountryOptions() {
  return Country.getAllCountries()
    .map((c) => ({ code: c.isoCode, name: c.name, flag: c.flag }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** ISO-2 -> country name (for submitting human-readable values). */
export function countryName(iso2) {
  return Country.getCountryByCode(iso2)?.name || iso2 || "";
}
